import React from "react";
import { createPortal } from "react-dom";
import { Button } from "nq-component";
import BasePage from "../../base/BasePage";
import InputFactory from "../../components/InputFactory";
import { upsertUseCase } from "../../usecases/object";
import { signInUseCase } from "../../usecases/user";
import {
  PASSWORD_TITLE,
  isStrongPassword,
  passwordInputProps,
  passwordStrength,
} from "../../components/InputFactory/passwordPolicy";

class ResetPasswordPage extends BasePage {
  constructor(props) {
    super(props);
    this.state = {
      progress: false,
      oldPassword: "",
      newPassword: "",
      confirmNewPassword: "",
      error: "",
    };
    this.onKeyDown = this.onKeyDown.bind(this);
  }

  componentDidMount() {
    this.root = document.getElementById("root");
    this.root?.setAttribute("inert", "");
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", this.onKeyDown);
  }

  componentWillUnmount() {
    this.root?.removeAttribute("inert");
    document.body.style.overflow = "";
    document.removeEventListener("keydown", this.onKeyDown);
  }

  onKeyDown(event) {
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  onChange(field, value) {
    this.setState({ [field]: value, error: "" });
  }

  async onSubmit(e) {
    e.preventDefault();
    const { oldPassword, newPassword, confirmNewPassword } = this.state;
    if (!oldPassword) {
      this.setState({ error: "Enter your current password." });
      return;
    }
    if (!isStrongPassword(newPassword)) {
      this.setState({ error: PASSWORD_TITLE });
      return;
    }
    if (newPassword !== confirmNewPassword) {
      this.setState({ error: "Password must be the same." });
      return;
    }

    const user = this.getCurrentUser();
    try {
      this.setState({ progress: true, error: "" });
      await signInUseCase().execute(
        {
          username: user.username,
          password: oldPassword,
        },
        { token: false },
      );
      await upsertUseCase().execute("users", {
        id: user.id,
        password: newPassword,
        isReset: true,
      });
      this.props.onComplete?.();
    } catch (error) {
      this.setState({
        error:
          error?.code === 404
            ? "Invalid current password."
            : error?.message || "Unable to update password.",
      });
    } finally {
      this.setState({ progress: false });
    }
  }

  render() {
    const { progress, newPassword, confirmNewPassword, oldPassword, error } =
      this.state;
    const strength = passwordStrength(newPassword);
    const confirmMismatch =
      confirmNewPassword.length > 0 && newPassword !== confirmNewPassword;
    const canSubmit =
      oldPassword &&
      isStrongPassword(newPassword) &&
      newPassword === confirmNewPassword &&
      !progress;

    const modal = (
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-password-title"
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 10000,
          background: "rgba(15, 23, 42, 0.55)",
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "center",
          overflowY: "auto",
          padding: "24px 16px",
        }}
      >
        <div
          style={{
            width: "100%",
            maxWidth: 480,
            margin: "auto",
            background: "#fff",
            borderRadius: 16,
            boxShadow: "0 24px 60px rgba(15, 23, 42, 0.25)",
            padding: "28px 28px 22px",
          }}
        >
          <h1
            id="create-password-title"
            style={{
              fontSize: 28,
              fontWeight: 700,
              color: "#111827",
              marginBottom: 8,
            }}
          >
            Create a Password
          </h1>
          <p style={{ color: "#6b7280", marginBottom: 22, lineHeight: 1.5 }}>
            Your password must include a mix of letters, numbers, and special
            characters to keep your account secure.
          </p>
          <form onSubmit={this.onSubmit.bind(this)}>
            <div className="mb-3">
              <label className="form-label fw-semibold">Current Password</label>
              <InputFactory
                type="Password"
                field="oldPassword"
                required
                placeholder="Current password"
                onChange={this.onChange.bind(this, "oldPassword")}
              />
            </div>
            <div className="mb-3 password-create-field">
              <label className="form-label fw-semibold" htmlFor="new-password">
                Password
              </label>
              <InputFactory
                id="new-password"
                type="Password"
                field="newPassword"
                required
                {...passwordInputProps({ placeholder: "" })}
                onChange={this.onChange.bind(this, "newPassword")}
              />
            </div>
            <div className="mb-3">
              <label className="form-label fw-semibold">Confirm Password</label>
              <InputFactory
                type="Password"
                field="confirmNewPassword"
                required
                placeholder="Re-type the new password"
                onChange={this.onChange.bind(this, "confirmNewPassword")}
              />
              {confirmMismatch && (
                <div className="text-danger fs-sm mt-1">
                  Password must be the same.
                </div>
              )}
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                marginTop: 4,
              }}
            >
              <div style={{ display: "flex", gap: 6, flex: 1 }}>
                {strength.rules.map((rule, index) => (
                  <span
                    key={rule.id}
                    style={{
                      height: 6,
                      flex: 1,
                      borderRadius: 99,
                      background:
                        index < strength.met ? strength.color : "#e5e7eb",
                    }}
                  />
                ))}
              </div>
              <span
                style={{
                  minWidth: 58,
                  textAlign: "right",
                  fontWeight: 600,
                  color: strength.label ? strength.color : "transparent",
                }}
              >
                {strength.label || "Weak"}
              </span>
            </div>
            <ul
              style={{
                listStyle: "none",
                padding: 0,
                margin: "16px 0 18px",
              }}
            >
              {strength.rules.map((rule) => (
                <li
                  key={rule.id}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 10,
                    marginBottom: 8,
                    color: "#374151",
                  }}
                >
                  <i
                    className={
                      rule.met ? "bi bi-check-circle-fill" : "bi bi-circle"
                    }
                    style={{
                      color: rule.met ? "#22c55e" : "#9ca3af",
                      marginTop: 2,
                    }}
                    aria-hidden="true"
                  />
                  <span>{rule.label}</span>
                </li>
              ))}
            </ul>
            {error && (
              <div className="alert alert-danger py-2 fs-sm" role="alert">
                {error}
              </div>
            )}
            <Button
              type="submit"
              className="btn btn-primary w-100"
              progress={progress}
              disabled={!canSubmit}
            >
              {progress ? "Please wait..." : "Update Password"}
            </Button>
          </form>
        </div>
        <style>{`
          .password-create-field .input-group {
            border: 1px solid #3b82f6;
            border-radius: 10px;
            overflow: hidden;
            background: #f8fafc;
          }
          .password-create-field .form-control {
            border: 0;
            background: transparent;
            box-shadow: none;
          }
          .password-create-field .form-control:focus {
            box-shadow: none;
          }
          .password-create-field .btn {
            border: 0;
            background: transparent;
          }
        `}</style>
      </div>
    );

    return createPortal(modal, document.body);
  }
}

export default ResetPasswordPage;
